import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Search, MapPin, Building, Filter, Plus, CheckCircle, AlertTriangle, XCircle, ArrowLeft } from 'lucide-react';
import { UserHeader } from '../components/UserHeader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MUNICIPIOS = [
  "Lomas de Zamora", "Lanús", "Avellaneda", "Almirante Brown", 
  "Esteban Echeverría", "Ezeiza", "Presidente Perón", "Quilmes", 
  "CABA", "La Plata", "San Vicente", "Cañuelas", "Otro"
];

const AREAS = [
  "Salud", "Desarrollo Social", "Educación", "Discapacidad",
  "Hábitat y Ambiente", "Niñez y Adolescencia", "Géneros y Diversidad",
  "Justicia", "Serv. Penitenciario", "Patronato Liberados", "Hábitat", "Investigación"
];

const TIPOS_INSTITUCION = [
  "Administración Pública Nacional",
  "Administración Pública Provincial",
  "Administración Pública Municipal",
  "Organización de la Sociedad Civil (ONG)",
  "Ámbito privado"
];

const AREA_COLORS = {
  "Salud": "bg-red-100 text-red-700",
  "Desarrollo Social": "bg-blue-100 text-blue-700",
  "Educación": "bg-yellow-100 text-yellow-700",
  "Discapacidad": "bg-purple-100 text-purple-700",
  "Hábitat y Ambiente": "bg-green-100 text-green-700",
  "Niñez y Adolescencia": "bg-pink-100 text-pink-700",
  "Géneros y Diversidad": "bg-violet-100 text-violet-700",
  "Justicia": "bg-indigo-100 text-indigo-700",
  "Serv. Penitenciario": "bg-gray-100 text-gray-700",
  "Patronato Liberados": "bg-orange-100 text-orange-700",
  "Hábitat": "bg-teal-100 text-teal-700",
  "Investigación": "bg-cyan-100 text-cyan-700"
};

const ESTADO_CONFIG = {
  activa: { label: "Activa", color: "bg-green-100 text-green-700", icon: CheckCircle },
  dudosa: { label: "Info. dudosa", color: "bg-yellow-100 text-yellow-700", icon: AlertTriangle },
  cerrada: { label: "Cerrada/Mudada", color: "bg-red-100 text-red-700", icon: XCircle }
};

export default function InstitucionesPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');

  const [filtros, setFiltros] = useState({
    municipio: searchParams.get('municipio') || '',
    localidad: '',
    area: searchParams.get('area') || '',
    tipo_institucion: ''
  });

  // Verificación de autenticación
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      buscarInstituciones();
    }
  }, [isAuthenticated]);

  // Loading mientras verifica auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const buscarInstituciones = async (filters = filtros, searchText = busqueda) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/instituciones/buscar`, {
        municipio: filters.municipio || null,
        localidad: filters.localidad || null,
        area: filters.area || null,
        tipo_institucion: filters.tipo_institucion || null,
        busqueda: searchText || null
      });
      setResults(response.data);
    } catch (err) {
      console.error('Error buscando instituciones:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    const newFiltros = { ...filtros, [field]: value };
    setFiltros(newFiltros);
    buscarInstituciones(newFiltros, busqueda);
  };

  const handleSearchChange = (value) => {
    setBusqueda(value);
    buscarInstituciones(filtros, value);
  };

  const limpiarFiltros = () => {
    const emptyFiltros = { municipio: '', localidad: '', area: '', tipo_institucion: '' };
    setFiltros(emptyFiltros);
    setBusqueda('');
    buscarInstituciones(emptyFiltros, '');
  };

  const hasFilters = filtros.municipio || filtros.localidad || filtros.area || filtros.tipo_institucion || busqueda;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header unificado */}
      <UserHeader />
      
      {/* Subtítulo de sección */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Building className="w-10 h-10 text-primary" />
            <h1 className="text-3xl font-bold text-gray-900">Recursero Institucional</h1>
          </div>
          <p className="text-gray-500">
            Guía territorial de instituciones para la articulación profesional
          </p>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Filtros */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-500" />
                <h2 className="font-semibold text-gray-900">Filtros</h2>
              </div>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={limpiarFiltros} className="text-gray-500">
                  Limpiar filtros
                </Button>
              )}
            </div>
            
            {/* Buscador */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar por nombre de institución, organismo o área..."
                  value={busqueda}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Select value={filtros.municipio} onValueChange={(v) => handleFilterChange('municipio', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Municipio" />
                </SelectTrigger>
                <SelectContent>
                  {MUNICIPIOS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={filtros.area} onValueChange={(v) => handleFilterChange('area', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Área de intervención" />
                </SelectTrigger>
                <SelectContent>
                  {AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={filtros.tipo_institucion} onValueChange={(v) => handleFilterChange('tipo_institucion', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo de institución" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_INSTITUCION.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>

              <Input
                type="text"
                placeholder="Localidad"
                value={filtros.localidad}
                onChange={(e) => handleFilterChange('localidad', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Resultados */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {loading ? 'Buscando...' : `${results.length} institución(es) encontrada(s)`}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : results.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron instituciones</h3>
              <p className="text-gray-500 mb-4">
                {hasFilters 
                  ? 'Probá ajustando los filtros de búsqueda'
                  : 'Aún no hay instituciones cargadas en el sistema'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((inst) => {
              const estadoConfig = ESTADO_CONFIG[inst.estado] || ESTADO_CONFIG.activa;
              const EstadoIcon = estadoConfig.icon;
              
              return (
                <Card 
                  key={inst.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/instituciones/${inst.id}`)}
                >
                  <CardContent className="pt-6">
                    {/* Header con estado */}
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 text-lg leading-tight flex-1 pr-2">
                        {inst.nombre}
                      </h3>
                      <Badge className={`${estadoConfig.color} flex items-center gap-1 shrink-0`}>
                        <EstadoIcon className="w-3 h-3" />
                        {estadoConfig.label}
                      </Badge>
                    </div>

                    {/* Ubicación */}
                    <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                      <MapPin className="w-4 h-4" />
                      <span>{inst.localidad ? `${inst.localidad}, ` : ''}{inst.municipio}</span>
                    </div>

                    {/* Tipo */}
                    <p className="text-sm text-gray-600 mb-3">{inst.tipo_institucion}</p>

                    {/* Áreas */}
                    {inst.areas && inst.areas.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {inst.areas.slice(0, 3).map(area => (
                          <Badge 
                            key={area} 
                            variant="secondary"
                            className={AREA_COLORS[area] || "bg-gray-100 text-gray-700"}
                          >
                            {area}
                          </Badge>
                        ))}
                        {inst.areas.length > 3 && (
                          <Badge variant="secondary" className="bg-gray-100 text-gray-500">
                            +{inst.areas.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Link */}
                    <div className="text-primary text-sm font-medium hover:underline">
                      Ver ficha →
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Botón volver */}
        <div className="text-center mt-8 pb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate('/recursero/seleccionar')}
            data-testid="back-to-recursero-select-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a selección
          </Button>
        </div>
      </main>
    </div>
  );
}
